import React__default from 'react';
import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { DateTime } from '@zapier/date';
import { createUniqueCustomProperties } from '../../../utils/createUniqueCustomProperty.es.js';
import { Animation } from '../../../theme/Animation.es.js';
import { Colors } from '../../../theme/Colors.es.js';
import { Shadows } from '../../../theme/Shadows.es.js';
import '../../../theme/Typography.es.js';
import { LegacyTypography } from '../../../theme/LegacyTypography.es.js';
import { Avatar } from '../../display/Avatar/Avatar.es.js';
import 'clsx';
import { AvatarsWithText, AvatarText } from '../../display/Avatar/AvatarsWithText.es.js';
import { Heading } from '../../display/Heading/Heading.es.js';
import { HeadingLevel } from '../../display/Heading/HeadingLevel.es.js';
import { Img } from '../../display/Img/Img.es.js';
import { Link } from '../../navigation/Link/Link.es.js';
import { Icon } from '../../display/Icon/Icon.es.js';
import { boxSizing, baseReset } from '../../../style-mixins/reset.es.js';

const labelBackgroundColorConfig = {
  primary: "UiPrimaryWeakest",
  secondary: "BrandPeach",
  neutral: "GrayWarm3"
};
const Vars = createUniqueCustomProperties("Article", [
  "imageMarginBottom",
  "titleMargin",
  "labelBackgroundColor"
]);
const Styles = labelStyles("Article", {
  root: css`
    ${boxSizing};
    display: flex;
    position: relative;
    background: ${Colors.PrimeWhite};
    border: 1px solid ${Colors.GrayWarm4};
    transition-property: box-shadow;
    transition-timing-function: ${Animation.transitionTimingFunction};
    transition-duration: ${Animation.transitionDuration};
    border-radius: 3px;
    &:hover,
    &:focus,
    &:focus-within {
      color: ${Colors.GrayWarm10};
      box-shadow: ${Shadows.elevation10};
    }

    &[data-layout='card'] {
      flex-direction: column;
    }

    &[data-layout='row'] {
      flex-direction: row;
      align-items: center;
      padding: 30px 0;
    }

    &[data-size='medium'] {
      ${Vars.imageMarginBottom}: 25px;
      ${Vars.titleMargin}: 20px;
      height: max-content;
    }
  `,
  image: css`
    ${boxSizing};
    position: relative;
    overflow: hidden;
    border-radius: 3px 3px 0 0;

    &[data-layout='card'] {
      margin-bottom: var(${Vars.imageMarginBottom});
      padding-bottom: 50%; // 2:1 ratio
    }

    &[data-layout='row'] {
      border-radius: 3px;
      margin-left: 30px;
      margin-right: 30px;
      width: 370px;
      height: 185px; // 2:1 ratio
    }
  `,
  // `Styles.image` creates a box with a 2:1 ratio as defined
  // by design and this node centers the image within that box.
  imageInner: css`
    ${boxSizing};
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 0;
    width: 100%;
  `,
  content: css`
    ${boxSizing};
    display: flex;
    flex-direction: column;
    border-radius: 0 0 3px 3px;
    padding: 0 20px 30px;
    flex: unset;

    &[data-showimg='false'] {
      padding: 30px 20px;
    }

    &[data-layout='row'] {
      padding: 0;
      border-radius: 0;
      flex: 1;
    }

    /* if the card is in row layout with no image we need to apply horizontal padding to the content */
    &[data-rownoimg='true'] {
      padding: 0 30px;
    }
  `,
  title: css`
    ${baseReset};
    &[class] {
      order: 2;
      ${LegacyTypography.sectionHeader3Semibold};
      color: ${Colors.GrayWarm10};
      margin-bottom: var(${Vars.titleMargin});
      transition: ${Animation.transitionValue};
      text-decoration: none;
      &:hover,
      &:focus {
        color: ${Colors.GrayWarm10};
      }

      // This pseudo element stretches over the entire Article
      // to make the entire Article clickable while preserving
      // accessibilty by not nesting <a /> tags.
      &::before {
        content: '';
        position: absolute;
        z-index: 1;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
      }

      &[data-lastelement='true'] {
        margin-bottom: 0;
      }
    }
  `,
  metaInfo: css`
    ${boxSizing};
    // Ensure meta info renders above rest of clickable Article
    // in case any of the meta info is interactive.
    position: relative;
    z-index: 2;
    order: 4;
    display: flex;
    align-items: center;
    color: ${Colors.GrayWarm7};
    ${LegacyTypography.smallPrint1};
  `,
  meta: css`
    ${boxSizing};
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  `,
  metaItem: css`
    ${boxSizing};
    display: flex;
    align-items: center;
    &:not(:last-child) {
      &::after {
        content: '•';
        margin: 0 5px;
      }
    }
  `,
  description: css`
    ${boxSizing};
    order: 3;
    margin-bottom: 20px;
    color: ${Colors.GrayWarm9};

    &[data-size='medium'] {
      ${LegacyTypography.paragraph3};
    }

    &[data-layout='row'] {
      ${LegacyTypography.paragraph1Short};
    }

    &[data-lastelement='true'] {
      margin-bottom: 0;
    }
  `,
  category: css`
    ${boxSizing};
    order: 1;
    position: relative;
    // Ensure clickable category renders above rest of clickable Article
    z-index: 2;
    ${LegacyTypography.smallPrint1Bold};
    margin-bottom: 10px;
  `,
  label: (labelVariant = "neutral") => css`
    ${boxSizing};
    order: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    text-align: center;
    ${Vars.labelBackgroundColor}: ${Colors[labelBackgroundColorConfig[labelVariant]]};
    background-color: var(${Vars.labelBackgroundColor});
    border-radius: 100px;
    ${LegacyTypography.paragraph3Semibold};
    color: ${Colors.GrayWarm10};
    padding: 4px 8px;
    margin-bottom: 10px;
    width: max-content;
  `
});
const ArticleTag = ({ label, labelIcon = "fileFilled", labelVariant = "neutral" }) => {
  return jsx(
    "span",
    { css: Styles.label(labelVariant), "data-zds": true },
    labelIcon && jsx(Icon, { name: labelIcon, size: 18 }),
    jsx("span", { "data-zds": true }, label)
  );
};
const Article = (props) => {
  return jsx(
    "article",
    { css: Styles.root, "data-layout": props.layout, "data-size": "medium", "data-zds": true },
    props.imageUrl && jsx(
      "div",
      { css: Styles.image, "data-layout": props.layout, "data-zds": true },
      jsx(
        "div",
        { css: Styles.imageInner, "data-zds": true },
        jsx(
          Img,
          {
            // `aria-hidden` and empty `alt` are applied since we do not describe
            // our images for screen readers. Understanding the content of the image
            // is not critical to understanding the content of the article.
            alt: "",
            "aria-hidden": true,
            aspectRatio: "2:1",
            borderRadius: "none",
            isBlock: true,
            loading: "lazy",
            objectFit: "cover",
            sizes: "100vw",
            src: props.imageUrl
          }
        )
      )
    ),
    jsx(
      "div",
      { css: Styles.content, "data-layout": props.layout, "data-showimg": !!props.imageUrl, "data-rownoimg": props.layout === "row" && !props.imageUrl, "data-zds": true },
      jsx(
        HeadingLevel,
        null,
        jsx(
          Link,
          { css: Styles.title, color: null, href: props.url, "data-lastelement": !props.author && !props.description },
          jsx(Heading, null, props.title)
        )
      ),
      props.description && jsx("div", { css: Styles.description, "data-size": "medium", "data-layout": props.layout, "data-lastelement": !props.author, "data-zds": true }, props.description),
      props.author && jsx(
        "div",
        { css: Styles.metaInfo },
        jsx(
          AvatarsWithText,
          { avatars: jsx(Avatar, { "aria-hidden": true, isBlock: true, name: props.author.name, size: "medium", url: props.author.imageUrl }), multiline: true, size: "medium" },
          jsx(AvatarText, { "aria-label": `by ${props.author.name}`, kind: "heavy" }, props.author.name),
          props.meta
        )
      ),
      props.label && jsx(ArticleTag, { label: props.label, labelIcon: props.labelIcon, labelVariant: props.labelVariant }),
      props.category && props.categoryUrl && !props.label && jsx(
        "div",
        { css: Styles.category, "data-zds": true },
        jsx(Link, { "aria-label": `Categorized under ${props.category}`, href: props.categoryUrl }, props.category)
      )
    )
  );
};
const ArticleMeta = (props) => {
  const children = React__default.Children.toArray(props.children).filter(Boolean).map((child, i) => {
    return jsx("span", { css: Styles.metaItem, key: i, "data-zds": true }, child);
  });
  return jsx(
    AvatarText,
    { kind: "light" },
    jsx("span", { css: Styles.meta, "data-zds": true }, children)
  );
};
const ArticleReadTime = (props) => {
  return jsx(
    "div",
    { "data-zds": true },
    props.minutes,
    " min read"
  );
};
const ArticleDate = (props) => {
  return jsx(DateTime, { date: props.date });
};

export { Article, ArticleDate, ArticleMeta, ArticleReadTime };
//# sourceMappingURL=Article.es.js.map
